import React from 'react';
import { cn } from 'shared/lib/cn';

export type Column<T> = {
  key: string;
  header: string;
  align?: 'left' | 'right' | 'center';
  render: (row: T) => React.ReactNode;
};

export type DataTableProps<T> = {
  caption: string;
  columns: Column<T>[];
  rows: T[];
  getRowKey: (row: T, index: number) => string;
  rowClassName?: (row: T) => string;
  /** Rows that are a rule between two groups of rows (the BOM's software /
   *  hardware split), not data: drawn as one line across every column. */
  isDivider?: (row: T) => boolean;
  footer?: React.ReactNode;
  className?: string;
  /** When given, rows become keyboard-accessible triggers: tabIndex 0, and
   *  Enter/Space fire it in addition to a click. Deliberately NOT
   *  role="button" — ARIA makes `button` children-presentational, which would
   *  collapse every <td> under the row into one opaque name and drop the
   *  row/table semantics a screen-reader user relies on to navigate a
   *  multi-column chooser like the Load list. The row keeps its implicit
   *  `row` role; give it a real name with `rowAriaLabel`. */
  onRowClick?: (row: T) => void;
  /** Accessible name for a clickable row (e.g. "ACME pilot — 7/1/2026"), since
   *  role="button" is no longer there to fold every cell into one name. */
  rowAriaLabel?: (row: T) => string;
};

// Right-aligned columns are figures: they never wrap and take only the width they
// need (`w-px` in an auto-layout table), so the text columns get the rest.
const ALIGN = { left: 'text-left', right: 'w-px whitespace-nowrap text-right', center: 'text-center' } as const;

export function DataTable<T>({ caption, columns, rows, getRowKey, rowClassName, isDivider, footer, className, onRowClick, rowAriaLabel }: DataTableProps<T>) {
  return (
    <div className={cn('overflow-x-auto rounded-[var(--radius-2)] border border-hairline/15', className)}>
      {/* The default text colour is declared HERE, on the table, and NOT on the
          cells: a colour set directly on a <td> always beats one inherited from
          its <tr>, which silently defeated `rowClassName` (the BOM marks
          custom-priced rows in the danger tone). DataTable.test.tsx fails if a
          cell-level colour class comes back. */}
      <table className="w-full border-collapse font-body text-sm text-ink">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr className="bg-surface-high">
            {columns.map((c) => (
              <th key={c.key} scope="col" className={cn('px-3 py-2 font-body text-sm font-semibold text-ink-muted', ALIGN[c.align ?? 'left'])}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            if (isDivider?.(row)) {
              return (
                <tr key={getRowKey(row, i)} aria-hidden="true">
                  <td data-testid="row-divider" colSpan={columns.length} className="p-0">
                    <div className="mx-2 border-t-2 border-hairline/40" />
                  </td>
                </tr>
              );
            }
            const clickable = onRowClick ? {
              tabIndex: 0,
              'aria-label': rowAriaLabel?.(row),
              onClick: () => onRowClick(row),
              onKeyDown: (e: React.KeyboardEvent<HTMLTableRowElement>) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onRowClick(row);
                }
              },
            } : undefined;
            return (
              <tr key={getRowKey(row, i)} className={cn('border-t border-hairline/10 hover:bg-surface-high/50', rowClassName?.(row))} {...clickable}>
                {columns.map((c) => (
                  <td key={c.key} className={cn('px-3 py-2 font-code text-sm', ALIGN[c.align ?? 'left'])}>
                    {c.render(row)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
        {footer && <tfoot className="border-t-2 border-hairline/25 bg-surface-raised">{footer}</tfoot>}
      </table>
    </div>
  );
}
